import React, { useState, useContext, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';

import ChangeTicketConfirmDialog from './ChangeTicketConfirmDialog';
import useApproveChangeTicket from '../hooks/vendors/useApproveChangeTicket';
import useVendorSearch from '../hooks/vendors/useVendorSearch';
import { AppContext } from '../../App';

interface ApproveChangeTicketProps {
  ticketId: number;
  vendorName: string;
  standardVendorName?: string | null;
}

const ApproveChangeTicket: React.FC<ApproveChangeTicketProps> = ({
  ticketId,
  vendorName,
  standardVendorName,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { setToast } = useContext(AppContext);

  // Only check whether the standard name already exists when the confirm
  // dialog is open, so this doesn't fetch on every row of the table.
  const { data: vendorMatches } = useVendorSearch({
    search: standardVendorName || '',
    limit: 5,
    enabled: open && !!standardVendorName?.trim(),
  });

  const isExistingVendor = useMemo(() => {
    const name = standardVendorName?.trim().toLowerCase();
    if (!name) return false;
    return (vendorMatches?.results || []).some(
      (vendor) => vendor.name.trim().toLowerCase() === name
    );
  }, [standardVendorName, vendorMatches]);

  const { mutate: approveChangeTicket, isPending: isLoading } = useApproveChangeTicket({
    onSuccess: () => {
      setOpen(false);
      setToast({
        variant: 'success',
        open: true,
        message: 'Change ticket approved successfully',
      });
    },
    onError: (error) => {
      console.error('Approve mutation error:', error);
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to approve change ticket. Please try again.',
      });
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false);
    }
  };

  const handleConfirm = () => {
    approveChangeTicket(ticketId);
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={
          !standardVendorName?.trim() ? 'Set standard vendor name first' : 'Approve Change Ticket'
        }
        disableFocusListener
        disableTouchListener
      >
        <IconButton
          data-cy={`btn-approve-change-ticket-${vendorName}`}
          onClick={handleClickOpen}
          disabled={isLoading || !standardVendorName?.trim()}
          sx={{
            color: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.main + '20',
            },
          }}
        >
          <CheckCircleIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <ChangeTicketConfirmDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isLoading={isLoading}
        action="approve"
        vendorName={vendorName}
        standardVendorName={standardVendorName || undefined}
        isExistingVendor={isExistingVendor}
      />
    </>
  );
};

export default ApproveChangeTicket;
